import { useEffect } from 'react';
import { useEtatReferentiel, useRechargerReferentiel } from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { Repousser } from './Repousser';

/**
 * Le carrefour, avant d'ouvrir le mail.
 *
 * Quand une commande n'a aucun produit reconnu, deux réparations sont possibles
 * et elles ne se valent pas : corriger les lignes répare la commande pour de
 * bon — le fournisseur sera déduit, la correspondance trouvée, et la prochaine
 * fois tout roulera —, tandis que rédiger le mail à la main ne règle que cet
 * envoi-ci. On pose donc la question, avec la première proposée en premier,
 * plutôt que d'ouvrir d'autorité l'une des deux fenêtres.
 */
export function ChoixCommande({
  commande, motif, onArticles, onMail, onFermer,
}: {
  commande: CommandeListee;
  /** Ce qui manque, dit en une phrase. */
  motif: string;
  onArticles: () => void;
  onMail: () => void;
  onFermer: () => void;
}) {
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  // Deux causes se ressemblent et ne se réparent pas pareil : ce produit-ci
  // n'a pas de correspondance, ou le référentiel n'en porte AUCUNE. Dans le
  // second cas, corriger la commande ne servirait à rien.
  const { data: referentiel } = useEtatReferentiel();
  const recharger = useRechargerReferentiel();
  const referentielIncomplet =
    referentiel !== undefined && referentiel.references > 0 && referentiel.correspondances === 0;

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div
        className="box" role="dialog" aria-modal="true"
        aria-label={`Commande incomplète ${commande.ref}`}
        style={{ borderTopColor: 'var(--jaune)' }}
      >
        <div className="mhead">
          <h2>Commande incomplète — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <p style={{ marginBottom: 14 }}>{motif}</p>

          {referentielIncomplet && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--rouge)', background: 'var(--brick-soft)' }}
            >
              <h3 style={{ color: 'var(--brick)' }}>Le référentiel n&apos;a aucune correspondance</h3>
              <p style={{ fontSize: 12.5, color: 'var(--brick)', margin: 0 }}>
                Ses {referentiel.references} références sont bien chargées, mais pas une seule ne
                porte de désignation fournisseur — <strong>toutes</strong> les commandes butent donc
                ici, pas seulement celle-ci. Ce n&apos;est pas la commande qu&apos;il faut corriger :
                lancez <span className="mono">pnpm seed --produits</span>, puis rechargez le
                référentiel ci-dessous — l&apos;import tourne dans un autre processus, l&apos;API ne
                le voit pas immédiatement.
              </p>
              <button
                type="button" className="btn sm" style={{ marginTop: 10 }}
                disabled={recharger.isPending}
                onClick={() => void recharger.mutateAsync()}
              >
                {recharger.isPending
                  ? <><span className="spin" /> Rechargement…</>
                  : 'Recharger le référentiel'}
              </button>
            </div>
          )}
          <p style={{ marginBottom: 14, fontSize: 13, color: 'var(--ink-2)' }}>
            Comment voulez-vous procéder ?
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button" className="btn pri"
              style={{ textAlign: 'left', padding: '11px 14px' }}
              onClick={onArticles}
            >
              Renseigner les produits
            </button>
            <p className="indice" style={{ marginTop: -4 }}>
              Cherchez les produits au référentiel et ajoutez-les à la commande — et si une
              référence y manque, créez-la au passage. Le fournisseur et sa désignation suivent tout
              seuls : la commande est réparée pour de bon, et la prochaine partira sans rien
              demander.
            </p>

            <button
              type="button" className="btn"
              style={{ textAlign: 'left', padding: '11px 14px' }}
              onClick={onMail}
            >
              Écrire le mail sans les produits
            </button>
            <p className="indice" style={{ marginTop: -4 }}>
              Le mail s&apos;ouvre avec une ligne « [À COMPLÉTER] » par produit manquant, quantité
              comprise, à remplacer par ce que le fournisseur reconnaît. Ne répare que cet envoi-ci :
              la commande suivante reposera la même question.
            </p>
          </div>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
